import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Settings2, ImageIcon, Inbox, ShieldCheck, Users, BellRing, UserCog } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const CLIENT_TABS = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "tab-dashboard" },
    { to: "/leads", label: "Leads", icon: Inbox, testid: "tab-leads" },
    { to: "/photos", label: "Photos", icon: ImageIcon, testid: "tab-photos" },
    { to: "/settings", label: "Business", icon: Settings2, testid: "tab-settings" },
    { to: "/account", label: "Account", icon: UserCog, testid: "tab-account" },
];

// Admin gets a focused 5-tab bar with the operational primary surfaces:
// Inbox (today's action queue), Clients (every tenant + intake), Admin (HQ
// tools — tenants table, Stripe events, change requests), Leads, Photos.
const ADMIN_TABS = [
    { to: "/admin/inbox", label: "Inbox", icon: BellRing, testid: "tab-inbox" },
    { to: "/admin/clients", label: "Clients", icon: Users, testid: "tab-clients" },
    { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "tab-admin" },
    { to: "/leads", label: "Leads", icon: Inbox, testid: "tab-leads" },
    { to: "/photos", label: "Photos", icon: ImageIcon, testid: "tab-photos" },
];

export default function BottomNav() {
    const { user } = useAuth();
    const tabs = user?.role === "admin" ? ADMIN_TABS : CLIENT_TABS;
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        if (user?.role !== "admin") return undefined;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await api.get("/admin/change-requests/pending-count");
                if (!cancelled) setPendingCount(Number(data?.count) || 0);
            } catch (_e) {
                /* silent */
            }
        };
        fetchCount();
        const onFocus = () => fetchCount();
        window.addEventListener("focus", onFocus);
        window.addEventListener("webjesters:requests-changed", onFocus);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("webjesters:requests-changed", onFocus);
        };
    }, [user?.role]);

    return (
        <nav
            className="portal-bottom-nav md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-zinc-800/80 bg-[#0a0a0a]/95 backdrop-blur-xl"
            data-testid="mobile-bottom-nav"
        >
            <div className={`grid h-full ${tabs.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}>
                {tabs.map(({ to, label, icon: Icon, testid }) => {
                    const isAdminTab = to === "/admin";
                    return (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/admin"}
                            data-testid={testid}
                            className={({ isActive }) =>
                                [
                                    "relative flex flex-col items-center justify-center gap-1 px-1 transition-colors",
                                    isActive ? "portal-tab-active" : "text-zinc-400 hover:text-zinc-200",
                                ].join(" ")
                            }
                        >
                            <div className="relative">
                                <Icon className="h-[18px] w-[18px] stroke-[1.75]" />
                                {isAdminTab && pendingCount > 0 && (
                                    <span
                                        data-testid="tab-admin-badge"
                                        title={`${pendingCount} unresolved`}
                                        className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 rounded-full bg-amber-500/25 text-amber-200 border border-amber-700/50 text-[9.5px] font-medium tabular-nums leading-none"
                                    >
                                        {pendingCount > 9 ? "9+" : pendingCount}
                                    </span>
                                )}
                            </div>
                            <span className="text-[10px] tracking-tight leading-none">{label}</span>
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}
